import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { setUserSession } from '@/entities/user/sessionSlice'
import { postApiAuthLogin } from '@/shared/apis/fastmile/generated/auth/auth'
import { TextField } from '@/shared/components/TextField/TextField'
import { useAppDispatch } from '@/shared/store'
import { Box, CircularProgress, FormControl, FormLabel, InputAdornment, Typography } from '@mui/material'

import { LoginCard } from '../../shared/components/LoginCard/LoginCard'
import {
  StyledAlert, StyledForm, StyledLink, StyledLogin, StyledSubmitButton, StyledTextFieldLabel,
} from '../../shared/components/LoginCard/StyledLogin'

export type LoginProps = {
  //
}

export const Login: React.FC<LoginProps> = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Check if it is redirected from the backend for email verification failure
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const hasEmailVerificationSuccess = searchParams.has('email-verification-success')
    const hasEmailVerificationFailed = searchParams.has('email-verification-failed')

    if (hasEmailVerificationFailed) {
      setError('Email verification failed - Token expired or invalid. Login again to resend a new verification email.')
    } else if (hasEmailVerificationSuccess) {
      setInfo('Email verification successed - Login to continue.')
    }
  }, [])

  const onLogin = async () => {
    setLoading(true)

    try {
      const result = await postApiAuthLogin({
        login: email,
        password: password,
        imei: '',
        info: '',
        client: '',
        deviceId: '',
        installId: '',
      })

      if (result?.token) {
        dispatch(setUserSession({
          token: result?.token,
          accessToken: result?.accessToken || null,
          refreshToken: result?.refreshToken || null,
          isEmailVerified: result?.isEmailVerified || false,
          email: result.userEmail || null,
        }))

        navigate('/operations', { replace: true })
      } else {
        setError('Invalid Login or Password')
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response?.data?.message)
      }
      else {
        setError(err.message)
      }
    }

    setLoading(false)
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onLogin()
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  return (
    <StyledLogin>
      <LoginCard>
        {error && <StyledAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </StyledAlert>}
        {info && <StyledAlert severity="success" sx={{ mb: 3 }}>
          {info}
        </StyledAlert>}
        <StyledForm onSubmit={onSubmit} >
          <FormControl required>
            <FormLabel htmlFor="email">
              Email
            </FormLabel>
            <TextField
              id="email"
              type="text"
              autoFocus
              required
              autoComplete="username email"
              aria-describedby="email"
              onChange={handleEmailChange}
              fullWidth
            />
          </FormControl>
          <FormControl required>
            <StyledTextFieldLabel htmlFor="password" >
              Password
            </StyledTextFieldLabel>
            <TextField
              id="password"
              type="password"
              required
              autoComplete="current-password"
              aria-describedby="password"
              onChange={handlePasswordChange}
              fullWidth
            />
          </FormControl>
          <StyledLink to="/account/request-password-reset">
            Forgot your password?
          </StyledLink>
          <StyledSubmitButton
            fullWidth
            size="medium"
            type="submit"
            disabled={isLoading}
            loading={isLoading}
            endIcon={
              <InputAdornment position="end">
                <CircularProgress size={'small'} />
              </InputAdornment>
            }
          >
            Login
          </StyledSubmitButton>
        </StyledForm>
        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            No account yet? {' '}
            <StyledLink to="/signup">
              Sign up
            </StyledLink>
          </Typography>
        </Box>
      </LoginCard>
    </StyledLogin>
  )
}